import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Award, Sparkles, Droplet, Scissors, Heart } from 'lucide-react';

function Services() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'Services',
        page_path: window.location.pathname,
      });
    }
  }, []);

  const services = [
    {
      icon: <Award className="w-8 h-8 text-primary" />,
      titleEN: "Signature Breed Cut",
      titleFR: "Coupe signature de race",
      taglineEN: "The excellence of breed standards",
      taglineFR: "L'excellence du standard de race",
      descEN: "A precision-focused grooming service tailored to specific breeds (Poodles, Schnauzers, Spaniels, etc.). We respect traditional lines and expert techniques to preserve and enhance your dog's authentic silhouette.",
      descFR: "Un service de toilettage de précision adapté aux races spécifiques (Caniches, Schnauzers, Spaniels, etc.). Nous respectons les lignes traditionnelles ainsi que les techniques artisanales afin de préserver et sublimer la silhouette authentique de votre chien.",
      image: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/bfhunz0v_372a452c-fa89-4be8-bbcc-13dd3eeab815.jpg'
    },
    {
      icon: <Sparkles className="w-8 h-8 text-primary" />,
      titleEN: "Full Grooming",
      titleFR: "Toilettage complet",
      taglineEN: "More than grooming — a true transformation.",
      taglineFR: "Bien plus qu'un toilettage — une véritable transformation.",
      descEN: "Treat your dog to a complete care experience that blends hygiene, comfort, and style. Our CasaPooch Signature Groom restores freshness and elegance with:",
      descFR: "Offrez à votre chien une expérience de soin complète, alliant hygiène, confort et élégance. Notre Toilettage Signature CasaPooch redonne fraîcheur et style à chaque compagnon grâce à :",
      featuresEN: [
        "A gentle, nourishing shampoo for a healthy, silky coat",
        "Ear cleaning and nail trimming for optimal well-being",
        "Sanitary trimming and paw pad care for added comfort",
        "Deep brushing and de-matting for a flawless finish",
        "Blueberry facial to soothe the skin, reduce stains, and gently remove impurities",
        "A customized haircut or shave tailored to your dog's style and needs"
      ],
      featuresFR: [
        "Un shampooing doux et nourrissant pour un pelage sain et soyeux",
        "Le nettoyage des oreilles et la coupe des griffes pour un bien-être optimal",
        "Une tonte hygiénique et le soin des coussinets pour plus de confort",
        "Un brossage et démêlage en profondeur pour une finition impeccable",
        "Un facial aux bleuets pour apaiser la peau, réduire les taches et éliminer les impuretés en douceur",
        "Une coupe ou tonte sur mesure, adaptée au style et aux besoins de votre chien"
      ],
      footerEN: "From messy to refined — grooming that combines care and precision.",
      footerFR: "Du négligé au soigné — un toilettage qui allie douceur et précision.",
      image: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/lyz9hkw7_IMG_3765_1.jpeg'
    },
    {
      icon: <Droplet className="w-8 h-8 text-primary" />,
      titleEN: "Bath & Touch-Up",
      titleFR: "Bain & retouches",
      taglineEN: "A touch of freshness for everyday elegance.",
      taglineFR: "Une touche de fraîcheur pour une élégance au quotidien.",
      descEN: "A revitalizing service between full grooms, designed to maintain your dog's cleanliness and comfort. Our Chic & Clean CasaPooch treatment refreshes and enhances your dog's coat with:",
      descFR: "Un service revitalisant entre deux toilettages complets, conçu pour maintenir la propreté et le confort de votre chien. Notre Chic & Propre CasaPooch redonne fraîcheur et éclat avec :",
      featuresEN: [
        "A gentle, nourishing shampoo for a healthy, silky coat",
        "Ear cleaning and nail trimming for optimal well-being",
        "Sanitary trimming and paw pad care for added comfort",
        "Deep brushing and de-matting for a clean, polished finish",
        "Blueberry facial to soothe the skin, reduce stains, and gently remove impurities"
      ],
      featuresFR: [
        "Un shampooing doux et nourrissant pour un pelage sain et soyeux",
        "Le nettoyage des oreilles et la coupe des griffes pour un bien-être optimal",
        "Une tonte hygiénique et le soin des coussinets pour plus de confort",
        "Un brossage et démêlage en profondeur pour une finition nette et soignée",
        "Un facial aux bleuets pour apaiser la peau, réduire les taches et éliminer les impuretés en douceur"
      ],
      footerEN: "A light, refreshing groom — perfect for keeping your dog clean and well-maintained between haircuts.",
      footerFR: "Un entretien léger et rafraîchissant — idéal pour garder votre chien propre entre deux coupes.",
      image: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/rnjig6db_CasaPooch-84.jpg'
    },
    {
      icon: <Heart className="w-8 h-8 text-primary" />,
      titleEN: "Bath & Beauty",
      titleFR: "Bain & beauté",
      taglineEN: "Bath & Brush Service",
      taglineFR: "Service de bain et brossage",
      descEN: "Give your dog a refreshing care session with our tailored bath and brush service. Whether your dog has a short coat for a clean finish or a double coat that requires deep washing and deshedding, we use gentle products and coat-specific brushing techniques to achieve a soft, lightweight result.",
      descFR: "Offrez à votre chien un soin rafraîchissant avec notre service de bain et brossage sur mesure. Qu’il s’agisse d’un pelage court pour une propreté impeccable ou d’un pelage double nécessitant un lavage en profondeur et un traitement anti-mue, nous utilisons des produits doux et des techniques de brossage adaptées à chaque type de poil pour un fini soyeux et léger.",
      footerEN: "Ideal for breeds such as Golden Retrievers, Pugs, Huskies, and Beagles.",
      footerFR: "Idéal pour des races comme le Golden Retriever, le Carlin, le Husky et le Beagle.",
      image: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/xv9ybmft_CasaPooch-85.jpg'
    },
    {
      icon: <Scissors className="w-8 h-8 text-primary" />,
      titleEN: "Nail Care & Paw Pad Hair",
      titleFR: "Soins des griffes & coussinets",
      taglineEN: "Essential care for your dog's comfort and health.",
      taglineFR: "Un soin essentiel pour le confort et la santé de votre chien.",
      descEN: "Precise trimming, filing, and paw check for a natural, pain-free stride.",
      descFR: "Coupe précise des griffes, limage, tonte des poils entre les coussinets et vérification des pattes pour une démarche naturelle et sans inconfort.",
      image: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/gyod7afa_CasaPooch-86.jpg'
    }
  ];

  return (
    <div className="services-page" data-testid="services-page">
      <section className="py-24 md:py-32 bg-warm-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="overline mb-4">{t('servicesPage.overline')}</div>
          <h1 className="font-playfair text-5xl md:text-7xl tracking-tighter leading-[1.1] font-medium text-charcoal mb-6" data-testid="services-title">
            {t('servicesPage.title1')}<br />{t('servicesPage.title2')}
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-charcoal/80">
            {t('servicesPage.subtitle')}
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-warm-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="space-y-24">
            {services.map((service, index) => (
              <motion.div
                key={service.titleEN}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="service-detail-card"
                data-testid={`service-${service.titleEN}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                  <div className={`service-detail-image-wrapper ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <img 
                      src={service.image}
                      alt={i18n.language === 'fr' ? service.titleFR : service.titleEN}
                      className="service-detail-image"
                      loading="lazy"
                    />
                  </div>

                  <div className={`service-detail-content ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className="w-14 h-14 rounded-full bg-accent/30 flex items-center justify-center mb-6">
                      {service.icon}
                    </div>
                    
                    <h2 className="font-playfair text-3xl md:text-4xl font-medium text-charcoal mb-3">
                      {i18n.language === 'fr' ? service.titleFR : service.titleEN}
                    </h2>
                    
                    <p className="text-lg text-primary font-medium mb-4 italic">
                      {i18n.language === 'fr' ? service.taglineFR : service.taglineEN}
                    </p>
                    
                    <p className="text-base leading-relaxed text-charcoal/80 mb-6">
                      {i18n.language === 'fr' ? service.descFR : service.descEN}
                    </p>

                    {service.featuresEN && (
                      <ul className="space-y-3 mb-6">
                        {(i18n.language === 'fr' ? service.featuresFR : service.featuresEN).map((feature) => (
                          <li key={feature} className="flex items-start space-x-3">
                            <span className="text-primary mt-1">•</span>
                            <span className="text-sm leading-relaxed text-charcoal/80">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {service.footerEN && (
                      <p className="text-sm leading-relaxed text-charcoal/70 italic border-l-2 border-primary pl-4">
                        {i18n.language === 'fr' ? service.footerFR : service.footerEN}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-accent/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-playfair text-4xl md:text-5xl tracking-tight leading-tight font-medium text-charcoal mb-6">
            {t('servicesPage.ctaTitle')}
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-charcoal/80 mb-8">
            {t('servicesPage.ctaText')}
          </p>
          <Link 
            to="/book" 
            className="inline-block px-12 py-4 rounded-full bg-primary hover:bg-charcoal text-white transition-colors duration-300 font-medium text-lg"
            data-testid="book-service-btn"
          >
            {t('nav.bookNow')}
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Services;
